import type {
  AgendaItem,
  MinutesOrdering,
  MotionType,
  User,
} from "@repo/types";
import { renderToString } from "react-dom/server";
import { MeetingMinutesHTML } from "@repo/ui/components/Base/MeetingMinutesHTML";
import { Delta } from "quill/core";
import { userStore } from "@/stores/userStore";
import { configRepo } from "@/repositories/configRepo";
import { firstValueFrom } from "rxjs";
import { tenantRepo } from "@/repositories/tenantRepo";
import { meetingRepo } from "@/repositories/meetingRepo";
import { teamRepo } from "@/repositories/teamRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { userRepo } from "@/repositories/userRepo";

/** Get agenda items mapped by  */
export const getMappedAgendaItems = async (
  tenantId: string,
  agendaItemIdMap: Record<string, number>
) => {
  const agendaItemMap = {} as Record<string, AgendaItem>;

  // collect agendaItems by version in id map
  await Promise.all(
    Object.keys(agendaItemIdMap).map(async (agendaItemId) => {
      const version = agendaItemIdMap[agendaItemId];
      const item = await firstValueFrom(
        agendaItemRepo.getVersion(tenantId, agendaItemId, version)
      );
      if (item) agendaItemMap[agendaItemId] = item;
    })
  );

  return agendaItemMap;
};

export const generateMeetingMinutesData = async ({
  tenantId,
  meetingId,
  order,
}: {
  tenantId: string;
  meetingId: string;
  order?: MinutesOrdering;
}): Promise<null | Delta | string> => {
  // get the tenant / meeting data
  const tenantData = await firstValueFrom(tenantRepo.get(tenantId));
  const meetingData = await firstValueFrom(
    meetingRepo.get(tenantId, meetingId)
  );

  if (!tenantData || !meetingData) return null;

  if (meetingData.minutesData?.json) {
    // minutes data was already saved: parse and return
    return JSON.parse(meetingData.minutesData.json) as Delta;
  }

  // generate new minutes data

  if (!meetingData.review?.itemVersions) return null;

  // get the team
  const teamData = await firstValueFrom(
    teamRepo.get(tenantId, meetingData.teamId ?? "")
  );
  if (!teamData) return null;

  // get users
  const users = await firstValueFrom(userRepo.list(tenantId));
  const userMap = users?.reduce(
    (map, user) => ((map[user.id] = user), map),
    Object.create(null) as Record<string, User>
  );

  // get motion types
  const motionTypes = await firstValueFrom(configRepo.get("motionTypes"));
  const motionTypeMap = {
    item: motionTypes.item.reduce(
      (obj, type) => ((obj[type.id] = type), obj),
      Object.create(null) as Record<string, MotionType>
    ),
    meeting: motionTypes.meeting.reduce(
      (obj, type) => ((obj[type.id] = type), obj),
      Object.create(null) as Record<string, MotionType>
    ),
  };

  // get the submitter
  const { user: currentUser, activeTeam } = userStore.getState();
  const submitter =
    currentUser?.id && activeTeam && activeTeam !== "org"
      ? activeTeam.teamMembers.find(
          (member) => member.userId === currentUser.id
        )
      : undefined;

  // get all agenda items
  const agendaItemMap = await getMappedAgendaItems(
    tenantId,
    meetingData.review.itemVersions
  );

  // generate minutes html
  return renderToString(
    <MeetingMinutesHTML
      enableLinks
      meeting={meetingData}
      agendaItemMap={agendaItemMap}
      motionTypes={motionTypeMap}
      tenant={tenantData}
      userMap={userMap}
      team={teamData}
      submitter={submitter}
      order={order ?? tenantData.minutesOrdering}
    />
  );
};
